import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { 
  Home, Briefcase, GraduationCap, 
  FolderKanban, Mail, BookOpen, Menu, X, Award
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
      
      // Only track sections on home page
      if (isHomePage) {
        // Order matches navbar and home sections
        const sections = ['hero', 'experience', 'education', 'recommendations', 'blogs', 'projects', 'contact'];
        const viewportCenter = window.innerHeight / 2;
        let currentSection = 'hero';
        let minDistance = Number.POSITIVE_INFINITY;

        for (const section of sections) {
          const el = document.getElementById(section);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          const sectionCenter = rect.top + rect.height / 2;
          const distance = Math.abs(sectionCenter - viewportCenter);
          // Prefer sections that overlap the viewport; as a fallback, pick closest center
          const overlapsCenter = rect.top <= viewportCenter && rect.bottom >= viewportCenter;
          if (overlapsCenter || distance < minDistance) {
            minDistance = distance;
            currentSection = section;
          }
        }

        // Ensure contact highlights when at page bottom (allow small tolerance for fractional pixels)
        const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 24;
        if (nearBottom) {
          setActiveSection('contact');
          return;
        }

        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Initial call to set the active section on mount
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isMobile) {
      if (menuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen, isMobile]);

  const handleNavClick = (path: string, sectionId: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Close mobile menu when clicked
    setMenuOpen(false);

    if (isHomePage) {
      // On home page, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // On other pages, navigate to the specific page
      if (path === '/') {
        // If going to home, navigate then scroll to section after a delay
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Navigate to non-home pages directly
        navigate(path);
      }
    }
  };

  const navItems = [
    { path: '/', name: 'Home', icon: <Home className="h-4 w-4" />, sectionId: 'hero' },
    { path: '/experience', name: 'Experience', icon: <Briefcase className="h-4 w-4" />, sectionId: 'experience' },
    { path: '/education', name: 'Education', icon: <GraduationCap className="h-4 w-4" />, sectionId: 'education' },
    { path: '/recommendations', name: 'Recommendations', icon: <Award className="h-4 w-4" />, sectionId: 'recommendations' },
    { path: '/blogs', name: 'Blogs', icon: <BookOpen className="h-4 w-4" />, sectionId: 'blogs' },
    { path: '/projects', name: 'Projects', icon: <FolderKanban className="h-4 w-4" />, sectionId: 'projects' },
    { path: '/contact', name: 'Contact', icon: <Mail className="h-4 w-4" />, sectionId: 'contact' },
  ];

  const isItemActive = (item: typeof navItems[0]) => {
    if (isHomePage) {
      // On home page, check if this is the active section
      return activeSection === item.sectionId;
    } else {
      // On other pages, check if this matches the current route
      return location.pathname === item.path;
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 py-4 transition-all duration-300">
      <div className="container max-w-6xl">
        <nav
          className={cn(
            "rounded-full px-5 sm:px-6 md:px-8 lg:px-10 py-2 transition-all duration-300 flex items-center justify-between md:justify-center overflow-x-auto",
            !isMobile ? "glass-morphism" : "bg-background",
            scrolled ? "shadow-lg" : ""
          )}
        >
          {/* Mobile fullscreen menu */}
          {isMobile && menuOpen && (
            <div className="fixed inset-0 bg-navy z-50 flex flex-col items-center pt-32">
              <div className="absolute top-6 right-6">
                <button
                  onClick={() => setMenuOpen(false)}
                  className="p-2 text-white"
                  aria-label="Close menu"
                >
                  <X className="h-7 w-7" />
                </button>
              </div>
              
              <div className="text-2xl font-bold mb-8">Shishir Regmi</div>
              
              <ul className="flex flex-col items-center space-y-6 w-full px-8">
                {navItems.map((item) => (
                  <li key={item.path} className="w-full text-center">
                    <a 
                      href={item.path} 
                      onClick={handleNavClick(item.path, item.sectionId)}
                      className={cn(
                        "nav-link flex flex-row items-center gap-3 py-3 px-2",
                        isItemActive(item) ? "" : ""
                      )}
                    >
                      <div className="text-teal">
                        {React.cloneElement(item.icon, { className: "h-8 w-8" })}
                      </div>
                      <span className="text-xl">{item.name}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ul
            className={cn(
              "flex md:justify-center md:items-center md:space-x-2 space-x-1 whitespace-nowrap",
              isMobile ? "hidden" : "flex-row"
            )}
          >
            {navItems.map((item) => (
              <li key={item.path}>
                <a 
                  href={item.path} 
                  onClick={handleNavClick(item.path, item.sectionId)}
                  className={cn(
                    "nav-link flex items-center gap-1.5",
                    isItemActive(item) ? "active" : ""
                  )}
                >
                  {item.icon}
                  <span>{item.name}</span>
                </a>
              </li>
            ))}
          </ul>

          {/* Mobile navigation display and toggle */}
          {isMobile && !menuOpen && (
            <>
              <div className="flex items-center">
                <a
                  href={navItems[0].path}
                  onClick={handleNavClick(navItems[0].path, navItems[0].sectionId)}
                  className={cn("nav-link flex items-center gap-1.5")}
                >
                  {navItems[0].icon}
                  <span>{navItems[0].name}</span>
                </a>
              </div>

              <button
                onClick={() => setMenuOpen(true)}
                className="p-2 text-white"
                aria-label="Open menu"
              >
                <Menu className="h-6 w-6" />
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
